import { Cart, CartAddress } from "types/cart";
import { RefurbishedUserContext } from "types/user";
import {
  PERCENTAGE,
  FIXED,
  STEP_ACCOUNT,
  STEP_CART,
  STEP_ADDRESS,
  STEP_SHIPPING,
  STEP_PRODUCT_COLLECTION,
  STEP_PAYMENT,
} from "./constants";

import { checkDateValidity } from "./steps/funnelStepShipping/calendar/utils";
import { getTrackerField } from "lib/ga";
import { newState } from "components/product/utils";

export const getProductsPrice = (cart: Cart): number => {
  return (
    (cart.shipping.price || 0) +
    cart.selection.reduce((totalPrice, item) => totalPrice + item.product.price + (item.warranty_price || 0), 0)
  );
};

export const getCurrentDiscount = (cart: Cart): number => {
  if (cart.coupon.discount_type === PERCENTAGE) {
    return Math.round((getProductsPrice(cart) * cart.coupon.discount_raw_amount) / 100);
  }
  if (cart.coupon.discount_type === FIXED) {
    return cart.coupon.discount_raw_amount;
  }
  return 0;
};

export const getCartPrice = (cart: Cart): number => {
  return Math.max(0, getProductsPrice(cart) - getCurrentDiscount(cart));
};

function validateCartStep(cart: Cart): boolean {
  if (cart.selection.every(({ warranty_pk }) => !!warranty_pk)) {
    return true;
  }
  return false;
}

export function hasCartNewAndRefurbishedProducts(cart: Cart): boolean {
  const appearances = cart.selection.map((item) => item.product.appearance_slug);

  return appearances.some((a) => a === newState) && appearances.some((a) => a !== newState);
}

function validateAccountStep(user: RefurbishedUserContext): boolean {
  if (user.isAuthenticated) {
    return true;
  }
  return false;
}

function validateAddressStep(address: CartAddress): boolean {
  if (address?.street && address?.zipcode && address.city && !!address.isBuilding) {
    if (!address.billing_adress_is_same) {
      if (
        address?.billing_street &&
        address?.billing_zipcode &&
        address.billing_city &&
        address?.billing_first_name &&
        address?.billing_last_name
      ) {
        return true;
      }
      return false;
    }
    return true;
  }
  return false;
}

function validateShippingStep(cart: Cart): boolean {
  if (cart.shipping?.delivery_option_type && cart.shipping?.date && cart.shipping?.slot) {
    return checkDateValidity(cart);
  }
  return false;
}
function validateProductCollectionStep(cart: Cart): boolean {
  return !!cart.collection_products;
}

export function validatedStep(cart: Cart, user: RefurbishedUserContext, allCartItemsAreAvailable: boolean): string {
  if (!allCartItemsAreAvailable) {
    return STEP_CART;
  }
  if (!validateCartStep(cart)) {
    return STEP_CART;
  }
  if (!validateAccountStep(user)) {
    return STEP_ACCOUNT;
  }
  if (!validateAddressStep(cart.shipping.address)) {
    return STEP_ADDRESS;
  }
  if (!validateShippingStep(cart)) {
    return STEP_SHIPPING;
  }
  if (!validateProductCollectionStep(cart)) {
    return STEP_PRODUCT_COLLECTION;
  }

  return STEP_PAYMENT;
}

function formatCollectionProductsPayload(collectionProducts) {
  let formattedCollectionProducts = [];
  collectionProducts.map((product) => {
    formattedCollectionProducts = [
      ...formattedCollectionProducts,
      {
        product_type: product.type,
        product_brand: product.brand,
        murfy_repaired: product.murfyRepaired.value,
        murfy_bought: product.murfyBought.value,
        is_broken: product?.isBroken?.value,
        problem: product?.isBroken?.value ? product.problem : null,
      },
    ];
  });
  return formattedCollectionProducts;
}

export function formatOrderPayload(cart, user) {
  const products = cart.selection.map((el) => {
    // warranty_name is still passed through warranty id data for transition
    return {
      id: el.product.id,
      warranty_id: el.warranty_name,
      warranty_pk: el.warranty_pk,
      delivery_option_id: el.delivery_option_id,
    };
  });
  const {
    address_id,
    street,
    city,
    zipcode,
    isBuilding,
    informations,
    elevator,
    difficulties,
    floor,
    billing_adress_is_same,
    billing_street,
    billing_city,
    billing_zipcode,
    billing_first_name,
    billing_last_name,
  } = cart.shipping.address;
  const payload = {
    has_opted_in: cart.acceptedOptIn,
    ga_client_id: getTrackerField(),
    products,
    delivery: {
      address_id,
      city,
      street,
      zipcode,
      is_building: isBuilding === "true",
      informations: informations || null,
      elevator: elevator === "true",
      difficulties: difficulties || null,
      floor: floor || null,
      date: cart.shipping.date,
      slot: cart.shipping.slot,
      delivery_option_id: cart.shipping.delivery_option_id,
      delivery_option_type: cart.shipping.delivery_option_type,
      local: cart.shipping.local,
      shipping_provider_id: cart.shipping.shipping_provider_id,
      recover_old_device: cart.shipping.recoverOldDevice || false,
      billing_name: billing_adress_is_same
        ? `${user.first_name} ${user.last_name}`
        : `${billing_first_name} ${billing_last_name}`,
      billing_city: billing_adress_is_same ? city : billing_city,
      billing_zipcode: billing_adress_is_same ? zipcode : billing_zipcode,
      billing_street: billing_adress_is_same ? street : billing_street,
      collection_products: cart.shipping.recoverOldDevice
        ? formatCollectionProductsPayload(cart?.collection_products)
        : [],
    },
    user: user,
    amount: getCartPrice(cart),
  };
  if (cart?.coupon?.id) {
    payload["coupon_id"] = cart.coupon.id;
  }
  return payload;
}
